// File: @/components/analytics/tracked-link.tsx

'use client';

import type { JSX, MouseEvent, ReactNode } from 'react';

import { ctaClickEvent, externalLinkClickEvent, socialClickEvent, track } from '@/lib/analytics';

type TrackedLinkKind = 'external' | 'social' | 'cta';
type SocialPlacement = 'footer' | 'header' | 'content';

interface TrackedLinkProps {
  readonly href: string;
  readonly children: ReactNode;
  readonly kind: TrackedLinkKind;
  readonly placement: string;
  readonly linkText?: string;
  readonly network?: string;
  readonly elementId?: string;
  readonly elementText?: string;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly id?: string;
  readonly rel?: string;
  readonly target?: string;
  readonly title?: string;
  readonly dataGtmParamGroupId?: string;
  readonly onClick?: () => void;
}

function getResolvedLinkText(linkText: string | undefined, href: string): string {
  return linkText ?? href;
}

function getResolvedCtaText(elementText: string | undefined, children: ReactNode): string {
  if (elementText !== undefined) {
    return elementText;
  }

  if (typeof children === 'string') {
    return children;
  }

  return '';
}

function resolveSocialPlacement(placement: string): SocialPlacement {
  if (placement === 'header') {
    return 'header';
  }

  if (placement === 'content') {
    return 'content';
  }

  return 'footer';
}

function trackLinkEvent(props: TrackedLinkProps): void {
  if (props.kind === 'external') {
    track(
      externalLinkClickEvent(
        props.href,
        getResolvedLinkText(props.linkText, props.href),
        props.placement,
      ),
    );
    return;
  }

  if (props.kind === 'social' && props.network !== undefined) {
    track(socialClickEvent(props.network, resolveSocialPlacement(props.placement)));
    return;
  }

  if (props.kind === 'cta' && props.elementId !== undefined) {
    track(
      ctaClickEvent(
        props.elementId,
        getResolvedCtaText(props.elementText, props.children),
        props.placement,
      ),
    );
  }
}

export function TrackedLink(props: TrackedLinkProps): JSX.Element {
  const handleClick = (_event: MouseEvent<HTMLAnchorElement>): void => {
    trackLinkEvent(props);

    if (props.onClick !== undefined) {
      props.onClick();
    }
  };

  return (
    <a
      aria-label={props.ariaLabel}
      className={props.className}
      data-gtm-param-group-id={props.dataGtmParamGroupId}
      href={props.href}
      id={props.id}
      rel={props.rel}
      target={props.target}
      title={props.title}
      onClick={handleClick}
    >
      {props.children}
    </a>
  );
}
